import { ArrowUpRight, CheckCircle2, Circle, ThumbsDown, ThumbsUp } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { ChartFrame } from "../../charts/ChartFrame";
import { SERIES, TOKEN_SERIES } from "../../charts/palette";
import { TimeChart } from "../../charts/TimeChart";
import { fmt, when } from "../../lib/format";
import { useInsights, useLinkInsights, useMe, useTests, useUsage } from "../../lib/queries";
import { Card } from "../../ui/Card";
import { EmptyState, ErrorState, PageSkeleton } from "../../ui/Feedback";
import { CodeBlock, Segmented } from "../../ui/Misc";
import { StatTile } from "../../ui/StatTile";
import { useProjectContext } from "./ProjectLayout";
import styles from "./SummaryTab.module.css";

const RANGES = [
  { value: 7, label: "7 hari" },
  { value: 30, label: "30 hari" },
  { value: 90, label: "90 hari" },
];

export function SummaryTab() {
  const { project, knowledge, embed, links: linkCounts } = useProjectContext();
  const me = useMe().data!;
  const [days, setDays] = useState(30);
  const usage = useUsage(project.id, days);
  const insights = useInsights(project.id, days);

  if (usage.isPending || insights.isPending) return <PageSkeleton />;
  if (usage.isError) return <ErrorState error={usage.error} />;
  if (insights.isError) return <ErrorState error={insights.error} />;

  const { totals, series } = usage.data;
  const info = insights.data;
  const handoffTotal = (info.handoffs.order ?? 0) + (info.handoffs.unknown ?? 0) + (info.handoffs.human ?? 0);
  const topMax = Math.max(1, ...info.topQuestions.map((q) => q.count));

  return (
    <div className={styles.grid} style={{ opacity: usage.isPlaceholderData ? 0.6 : 1 }}>
      <div className={styles.toolbar}>
        <Segmented label="Rentang waktu" value={days} options={RANGES} onChange={setDays} />
      </div>

      {project.status !== "active" && me.role !== "client" && (
        <Onboarding projectId={project.id} knowledgeEntries={knowledge.entries} published={project.publishedVersion !== null} linkage={project.linkage.enabled} links={linkCounts.active} />
      )}

      <div className={styles.tiles}>
        <StatTile label="Percakapan" value={fmt.int(totals.conversations)} sub={`${info.turnsPerConversation} pertanyaan per percakapan`} trend={series.map((d) => d.conversations)} />
        <StatTile
          label="Biaya per percakapan"
          value={fmt.rupiah(usage.data.costPerConversationMilli)}
          sub={`estimasi katalog ${fmt.rupiah(usage.data.estimatedCostPerConversationMilli)}${project.linkage.enabled ? " (dengan Linkage)" : ""}`}
        />
        <StatTile
          label="Cache hit"
          value={fmt.percent(usage.data.cacheHitRatio)}
          sub={usage.data.cacheHitRatio === 0 ? "prompt < 1.024 token: belum di-cache penyedia" : "porsi input dibaca dari cache"}
        />
        <StatTile
          label="Dialihkan ke WhatsApp"
          value={fmt.int(handoffTotal)}
          sub={`${fmt.int(info.handoffs.order ?? 0)} siap pesan · ${fmt.int(info.handoffs.unknown ?? 0)} tak terjawab`}
        />
        <StatTile
          label="Jawaban membantu"
          value={info.feedback.up + info.feedback.down ? fmt.percent(info.feedback.up / (info.feedback.up + info.feedback.down)) : "—"}
          sub={`${fmt.int(info.feedback.up)} 👍 · ${fmt.int(info.feedback.down)} 👎 dari pengunjung`}
        />
        {project.linkage.enabled && <LinkageTile projectId={project.id} days={days} />}
      </div>

      <div className={styles.twoCol}>
        <Card title="Token per hari" description="Dipisah per jenis karena harganya berbeda: output 5× input, cache read 0,1×.">
          <ChartFrame
            legend={TOKEN_SERIES.map((s) => ({ label: s.label, color: s.color }))}
            table={{
              columns: ["Tanggal", ...TOKEN_SERIES.map((s) => s.label), "Biaya"],
              rows: series.map((d) => [when.day(d.day), ...TOKEN_SERIES.map((s) => fmt.int(d[s.key])), fmt.rupiah(d.costMilli)]),
            }}
          >
            <TimeChart kind="stacked" data={series} series={[...TOKEN_SERIES]} format={fmt.compact} ariaLabel="Token per hari menurut jenis" />
          </ChartFrame>
        </Card>
        <Card title="Percakapan per hari" description="Widget dan API, tanpa playground dan uji terima.">
          <ChartFrame table={{ columns: ["Tanggal", "Percakapan", "Jawaban AI"], rows: series.map((d) => [when.day(d.day), fmt.int(d.conversations), fmt.int(d.messages)]) }}>
            <TimeChart kind="area" data={series} series={[{ key: "conversations", label: "Percakapan", color: SERIES.blue }]} format={fmt.int} ariaLabel="Percakapan per hari" />
          </ChartFrame>
        </Card>
      </div>

      <div className={styles.twoCol}>
        <Card
          title="Pertanyaan terpopuler"
          description="Dikelompokkan menurut butir pengetahuan yang menjawabnya. Yang tidak punya butir = celah pengetahuan."
          actions={
            me.role !== "client" && (
              <Link to="../knowledge" relative="path" className={styles.inlineLink}>
                Kelola pengetahuan <ArrowUpRight aria-hidden />
              </Link>
            )
          }
        >
          {info.topQuestions.length === 0 ? (
            <EmptyState title="Belum ada pertanyaan pada rentang ini" />
          ) : (
            <ol className={styles.questions}>
              {info.topQuestions.map((cluster) => (
                <li key={cluster.label}>
                  <div className={styles.questionHead}>
                    <span className={styles.questionLabel}>{cluster.label}</span>
                    <span className={`${styles.questionCount} num`}>{fmt.int(cluster.count)}</span>
                  </div>
                  <div className={styles.bar}>
                    <span style={{ width: `${(cluster.count / topMax) * 100}%`, background: cluster.entryId ? "var(--series-1)" : "var(--serious)" }} />
                  </div>
                  <div className={styles.questionExamples}>
                    {cluster.entryId ? "Contoh: " : "Belum ada jawaban · contoh: "}
                    {cluster.examples.map((example) => `“${example}”`).join(", ")}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>

        <div className={styles.stack}>
          <Card title="Penilaian pengunjung">
            <div className={styles.feedback}>
              <div>
                <ThumbsUp aria-hidden />
                <strong>{fmt.int(info.feedback.up)}</strong>
                <span>membantu</span>
              </div>
              <div>
                <ThumbsDown aria-hidden />
                <strong>{fmt.int(info.feedback.down)}</strong>
                <span>kurang tepat</span>
              </div>
            </div>
          </Card>
          {embed && me.role !== "client" && (
            <Card title="Kode pemasangan widget" description="Satu baris, dimuat asinkron. Tempel sebelum </body>.">
              <CodeBlock code={embed} />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

/** Linkage at a glance: how often a suggested referral is followed. */
function LinkageTile({ projectId, days }: { projectId: string; days: number }) {
  const insights = useLinkInsights(projectId, days);
  const totals = insights.data?.totals ?? { offered: 0, clicks: 0 };
  return <StatTile label="Rujukan diklik" value={fmt.int(totals.clicks)} sub={`dari ${fmt.int(totals.offered)} ditawarkan${totals.offered ? ` · ${fmt.percent(totals.clicks / totals.offered)}` : ""}`} />;
}

/** Go-live checklist mirroring the AI implementation flow in Buku Panduan 12.2. */
function Onboarding({ projectId, knowledgeEntries, published, linkage, links }: { projectId: string; knowledgeEntries: number; published: boolean; linkage: boolean; links: number }) {
  const tests = useTests(projectId);
  const reviewed = tests.data?.runs.some((run) => run.status === "reviewed") ?? false;
  const steps = [
    { done: knowledgeEntries > 0, label: "Isi basis pengetahuan dari materi klien", to: "../knowledge" },
    { done: published, label: "Terbitkan versi pengetahuan", to: "../knowledge" },
    ...(linkage ? [{ done: links > 0, label: "Susun daftar rujukan Linkage (impor dari template, sitemap, atau feed)", to: "../links" }] : []),
    { done: (tests.data?.cases.length ?? 0) >= 30, label: "Siapkan 30 pertanyaan uji (20 umum + 10 jebakan)", to: "../tests" },
    { done: reviewed, label: linkage ? "Uji terima bersama PIC klien, termasuk uji rujukan, cetak Berita Acara" : "Uji terima bersama PIC klien, cetak Berita Acara", to: "../tests" },
    { done: false, label: "Isi saldo awal, pasang widget, lalu aktifkan chatbot", to: "../settings" },
  ];
  return (
    <Card title="Menuju tayang" description="Chatbot ini belum aktif. Selesaikan langkah berikut.">
      <ol className={styles.steps}>
        {steps.map((step) => (
          <li key={step.label} className={step.done ? styles.stepDone : ""}>
            {step.done ? <CheckCircle2 aria-hidden /> : <Circle aria-hidden />}
            <Link to={step.to} relative="path">
              {step.label}
            </Link>
          </li>
        ))}
      </ol>
    </Card>
  );
}
